import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveBrandLogo, handleBrandLogoError } from '../utils/brandLogo';

// Captain order placement now lives in the consumer app on port 3008.
// Build target URL from current host so it works in dev/UAT/prod without config.
const captainOrderingUrl = () => {
  if (typeof window === 'undefined') return '#';
  return `${window.location.protocol}//${window.location.hostname}:3008/captain/login`;
};

const navItems = [
  {
    path: '/captain/dashboard',
    icon: 'bi bi-speedometer2',
    label: 'Dashboard',
    exact: true
  },
  {
    external: true,
    href: captainOrderingUrl,
    icon: 'bi bi-grid-3x3-gap-fill',
    label: 'Take Order'
  },
  {
    path: '/captain/operations/orders',
    icon: 'bi bi-receipt-cutoff',
    label: 'Order Log',
    exact: true
  }
];

const CaptainSidebar = ({ collapsed, visible, onLogout }) => {
  const location = useLocation();
  const { logoUrl, feviconUrl, restaurantName } = useTheme();
  const { isImpersonating, returnToSuperAdmin } = useAuth();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserName(userData.name || '');
        setUserRole(userData.role || '');
      } catch (e) {
        // ignore malformed local storage payload
      }
    }

    setUserName((prev) => prev || localStorage.getItem('UserName') || '');
    setUserRole((prev) => prev || localStorage.getItem('UserRole') || 'captain');
  }, []);

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className={`dms-sidebar ${collapsed ? 'collapsed' : ''} ${visible ? 'show' : ''}`}>
      {isImpersonating && (
        <button
          type="button"
          className="sidebar-impersonation-btn"
          onClick={returnToSuperAdmin}
          title={collapsed ? 'Back to Super Admin' : ''}
        >
          <i className="bi bi-arrow-left-circle-fill"></i>
          {!collapsed && (
            <span className="sidebar-impersonation-text">
              <span className="sidebar-impersonation-label">Back to Super Admin</span>
              <span className="sidebar-impersonation-sub">Viewing as {userName || 'Captain'}</span>
            </span>
          )}
        </button>
      )}

      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-logo">
            <img
              src={resolveBrandLogo(logoUrl, feviconUrl)}
              alt={restaurantName || 'Captain Panel'}
              className="sidebar-logo-img"
              onError={handleBrandLogoError(feviconUrl)}
            />
          </div>
        </div>
      </div>

      <div className="sidebar-user-card" title={collapsed ? `${userName || 'Captain'} - ${userRole || 'captain'}` : ''}>
        <div className="sidebar-user-avatar">
          {(userName || 'C').charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{userName || 'Captain'}</span>
            <span className="sidebar-user-role">{(userRole || 'captain').replace(/_/g, ' ')}</span>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <div key={item.path || item.label} className="nav-item">
            {item.external ? (
              <a
                href={typeof item.href === 'function' ? item.href() : item.href}
                className="nav-link"
                title={collapsed ? item.label : ''}
              >
                <i className={item.icon}></i>
                {!collapsed && <span className="nav-label">{item.label}</span>}
              </a>
            ) : (
              <Link
                to={item.path}
                className={`nav-link ${isActive(item.path, item.exact) ? 'active' : ''}`}
                title={collapsed ? item.label : ''}
              >
                <i className={item.icon}></i>
                {!collapsed && <span className="nav-label">{item.label}</span>}
              </Link>
            )}
          </div>
        ))}
      </nav>

      <a
        href="https://webdekho.in"
        target="_blank"
        rel="noopener noreferrer"
        className="managed-by-sidebar"
        title="Webdekho"
      >
        <img src="https://webdekho.in/images/wpl_rms.png" alt="Webdekho" className="managed-by-logo" />
      </a>

      <div className="sidebar-logout">
        <div className="nav-item">
          <div
            className="nav-link"
            onClick={onLogout}
            style={{ cursor: 'pointer' }}
            title={collapsed ? 'Logout' : ''}
          >
            <i className="bi bi-box-arrow-right"></i>
            {!collapsed && <span className="nav-label">Logout</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptainSidebar;
